import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Heading,
  VStack,
  useToast,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { Eye,EyeOff  } from 'lucide-react';
import { empLogin } from '../redux/api/admin/authApi';
import { useNavigate } from 'react-router-dom';
import { Link } from "react-router-dom";
import { savePermissions } from '../../utils/IndexDB/PermissionStore';
import { usePermission } from '../../context/PermissionContext';


export default function AdminLogin() {
  const toast = useToast();
  const { refreshPermissions } = usePermission();
  const [hover, setHover] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Clear field-specific error when user types
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ''
      }));
    }
  };

  // Toggle password visibility
  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({
      email: '',
      password: '',
      general: ''
    });

    // Basic validation
    let hasError = false;
    const newErrors = { ...errors };

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      hasError = true;
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Call the empLogin API
      const response = await empLogin({
        email: formData.email,
        password: formData.password,
      });

      console.log("responsee",response);
      

      // Assuming the API returns data on successful login
      if (response && response.data.success) {
        toast({
          title: "Success",
          description: "Login successful!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Store token or user data if needed
        if (response.data.authToken) {
          console.log("authToken",response.data.authToken);
          
          localStorage.setItem('adminToken', response.data.authToken);
          localStorage.setItem('authToken', response.data.authToken);
          localStorage.setItem('adminUser', JSON.stringify(response.data.user));
          localStorage.setItem('loggedEmployee', response.data.user.ROWID);
          const allData = localStorage.getItem('adminUser');
          console.log("allData",allData);
          const permissionsSaved = await savePermissions(response.data.user);
          if (!permissionsSaved) {
            throw new Error("Unable to save user permissions. Please try logging in again.");
          }
          await refreshPermissions();
        }
        
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different error scenarios
      let errorMessage = "Login failed. Please try again.";
      let backendMessage = "";
      
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data;
        
        // Extract backend error message
        if (errorData?.msg) {
          backendMessage = errorData.msg;
          errorMessage = errorData.msg;
        } else if (errorData?.message) {
          backendMessage = errorData.message;
          errorMessage = errorData.message;
        } else if (errorData?.error) {
          // If error is an object with message property
          if (typeof errorData.error === 'object' && errorData.error.message) {
            backendMessage = errorData.error.message;
            errorMessage = errorData.error.message;
          } else if (typeof errorData.error === 'string') {
            backendMessage = errorData.error;
            errorMessage = errorData.error;
          }
        }
        
        // Handle specific backend error codes
        if (error.response.status === 400) {
          errorMessage = backendMessage || "Invalid email or password";
        } else if (error.response.status === 401) {
          errorMessage = "Unauthorized access";
        } else if (error.response.status === 404) {
          errorMessage = "User not found";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later";
        }
        
      } else if (error.request) {
        // Request was made but no response
        errorMessage = "No response from server. Please check your connection.";
        backendMessage = "Network error - server not responding";
      } else {
        // Something happened in setting up the request
        backendMessage = error.message;
      }
      
      // Set the general error for display
      setErrors(prev => ({
        ...prev,
        general: backendMessage
      }));
      
      toast({
        title: "Login Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minH="100vh"
      bg="gray.50"
    >
      <Box
        bg="white"
        px={8}
        py={14}
        rounded="xl"
        shadow="md"
        w="lg"
        mb={16}
      >
        <Heading textAlign="center" mb={6}>
          Admin Login
        </Heading>

        {/* Display general error if exists */}
        {errors.general && (
          <Box 
            mb={4} 
            p={3} 
            bg="red.50" 
            borderLeft="4px solid" 
            borderColor="red.500"
            color="red.700"
            fontSize="sm"
            borderRadius="md"
          >
            {errors.general}
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl id="email" isRequired isInvalid={!!errors.email}>
              <FormLabel>Email address</FormLabel>
              <Input 
                type="email" 
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
              {errors.email && (
                <Box color="red.500" fontSize="sm" mt={1}>
                  {errors.email}
                </Box>
              )}
            </FormControl>

            <FormControl id="password" isRequired isInvalid={!!errors.password}>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    icon={showPassword ? <Eye  /> : <EyeOff />}
                    onClick={handlePasswordVisibility}
                    variant="ghost"
                    size="sm"
                    isDisabled={loading}
                  />
                </InputRightElement>
              </InputGroup>
              {errors.password && (
                <Box color="red.500" fontSize="sm" mt={1}>
                  {errors.password}
                </Box>
              )}
              {/* <FormLabel>Password</FormLabel> */}
              <Link to="/admin-forgot-password">
                  <span style={{ cursor: "pointer", color: "blue",justifyContent:"end" }}>Forgot Password</span>
                </Link>
            </FormControl>

            <Button 
              type="submit"
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              style={{
                width: "100%",
                padding: "12px 0",
                backgroundColor: hover ? "#ffffff" : "#011638",
                color: hover ? "#000000" : "#ffffff",
                fontWeight: "700",
                border: hover ? "1px solid #011638" : "none",
                borderRadius: "30px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "1rem",
                boxShadow: "0 2px 6px rgba(1, 22, 56, 0.3)",
                transition: "all 0.3s ease-in-out",
                opacity: loading ? 0.7 : 1,
              }}
              disabled={loading}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(1, 22, 56, 0.4)",
              }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" mr={2} />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </Button>
          </VStack>
        </form>
      </Box>
    </Box>
  );
}
